import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api/client.js";
import { colors } from "../utils/theme.js";
import { inputStyle, primaryBtn, ghostBtn } from "../clients/ui.js";
import ConfirmDialog from "../clients/ConfirmDialog.jsx";
import { localToday } from "../meetings/period.js";
import { STATUS_ORDER, STATUS_META, PRIORITY_ORDER, PRIORITY_META, CLIENT_OWNER, isClientOwner, trackCounts, sortTracks, isOverdue, searchContacts } from "./crmLogic.js";
import ContactForm from "./ContactForm.jsx";
import ContactCard from "./ContactCard.jsx";
import ImportCsv from "./ImportCsv.jsx";
import TrackPanel from "./TrackPanel.jsx";
import UserAvatar from "./UserAvatar.jsx";
import Modal from "./Modal.jsx";

export const ClientTag = () => <span style={{ fontSize: 10, fontWeight: 700, color: colors.warning, letterSpacing: 0.4 }}>CLIENT</span>;
const fmtDate = (d) => new Date(`${d}T12:00:00Z`).toLocaleDateString("en-US", { timeZone: "UTC", month: "short", day: "numeric" });

// A client's Mini CRM tab (spec 2026-11-03): this client's own tracks with contacts from the
// shared book. Saves are optimistic and revert (with a flash) on failure.
export default function ClientCrm({ clientId, clientName, users = [], flash, initialTrackId = null, onInitialTrackUsed }) {
  const [tracks, setTracks] = useState([]);
  const [allTracks, setAllTracks] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [clients, setClients] = useState([]);
  const [status, setStatus] = useState("loading");
  const [openId, setOpenId] = useState(initialTrackId);
  const [statusFilter, setStatusFilter] = useState("");
  const [ownerFilter, setOwnerFilter] = useState("");
  const [modal, setModal] = useState(null); // "new" | "link" | "import"
  const [linkQuery, setLinkQuery] = useState("");
  const [editContactId, setEditContactId] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const loadedRef = useRef(false);
  const today = localToday();

  const load = useCallback(async () => {
    const [ts, all, cs, cl] = await Promise.all([
      api.crmTracks.listAll({ clientId }), api.crmTracks.listAll(), api.contacts.listAll(), api.clients.list().catch(() => null),
    ]);
    if (!ts || !cs) { if (loadedRef.current) flash?.("error", "Couldn’t refresh the CRM."); else setStatus("error"); return; }
    loadedRef.current = true;
    setTracks(ts); setAllTracks(all || ts); setContacts(cs); setClients(cl || []); setStatus("ready");
  }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  // Deep link: open the linked track, then tell the page it's been used (so it isn't reopened).
  useEffect(() => { if (initialTrackId) { setOpenId(initialTrackId); onInitialTrackUsed?.(); } }, [initialTrackId]); // eslint-disable-line react-hooks/exhaustive-deps

  const contactOf = useCallback((t) => contacts.find((c) => c.id === t.contactId) || null, [contacts]);
  const counts = useMemo(() => trackCounts(tracks, today), [tracks, today]);
  const shown = useMemo(() => sortTracks(
    tracks.filter((t) => (!statusFilter || t.status === statusFilter) && (!ownerFilter || (ownerFilter === "__none" ? !t.ownerUserId : t.ownerUserId === ownerFilter))),
    today, (t) => contactOf(t)?.name), [tracks, statusFilter, ownerFilter, today, contactOf]);
  const open = tracks.find((t) => t.id === openId);
  const user = (id) => users.find((u) => u.id === id || u.email === id);
  const clientNameOf = (id) => clients.find((c) => c.id === id)?.name || "another client";

  const replace = (doc) => { setTracks((ts) => ts.map((t) => (t.id === doc.id ? doc : t))); setAllTracks((ts) => ts.map((t) => (t.id === doc.id ? doc : t))); };
  const save = async (id, fields) => {
    const prev = tracks.find((t) => t.id === id);
    replace({ ...prev, ...fields });
    const r = await api.crmTracks.update(id, fields);
    if (r.ok) { replace(r.data); return true; }
    replace(prev);
    flash?.("error", r.data?.error || "Couldn’t save that change.");
    return false;
  };
  const activityOp = async (call) => {
    const r = await call();
    if (r.ok) { replace(r.data); return true; }
    flash?.("error", r.data?.error || "Couldn’t save the activity.");
    return false;
  };
  const createTrack = async (contactId) => {
    const r = await api.crmTracks.create({ contactId, clientId });
    setModal(null);
    if (r.ok) { setTracks((ts) => [...ts, r.data]); setAllTracks((ts) => [...ts, r.data]); setOpenId(r.data.id); flash?.("success", "Added to this client’s CRM"); return; }
    if (r.status === 409 && r.data?.existingId) { setOpenId(r.data.existingId); return; }
    flash?.("error", r.data?.error || "Couldn’t add the contact.");
  };
  const remove = async (t) => {
    setConfirmRemove(null); setOpenId(null);
    setTracks((ts) => ts.filter((x) => x.id !== t.id));
    const r = await api.crmTracks.remove(t.id);
    if (!r.ok) { setTracks((ts) => [...ts, t]); flash?.("error", "Couldn’t remove the contact."); }
    else flash?.("success", "Removed from this client’s CRM");
  };

  const onClient = new Set(tracks.map((t) => t.contactId));
  const linkable = searchContacts(contacts.filter((c) => !onClient.has(c.id)), linkQuery).slice(0, 50);
  // Row dropdowns (priority, status, owner) edit in place; their clicks never open the panel.
  const stop = (e) => e.stopPropagation();
  const pill = { fontSize: 12, border: `1px solid ${colors.border}`, borderRadius: 20, padding: "3px 8px", background: colors.surfaceAlt, fontFamily: "inherit", cursor: "pointer", outline: "none", maxWidth: 160 };
  const opt = { color: colors.text, background: colors.surface };
  const th = { textAlign: "left", color: colors.textDim, fontWeight: 500, fontSize: 11.5, textTransform: "uppercase", letterSpacing: 0.5, padding: 8, borderBottom: `1px solid ${colors.border}` };
  const td = { padding: "9px 8px", borderBottom: `1px solid ${colors.border}`, fontSize: 13, color: colors.text, verticalAlign: "middle" };
  const stat = (key, label, value, warn) => (
    <div data-testid={`crm-count-${key}`} style={{ background: colors.surfaceAlt, borderRadius: 8, padding: "8px 14px", fontSize: 12, color: colors.textMuted }}>
      <b style={{ display: "block", fontSize: 18, color: warn ? colors.danger : colors.text }}>{value}</b>{label}
    </div>
  );

  if (status === "loading") return <div style={{ color: colors.textDim, fontSize: 14 }}>Loading CRM…</div>;
  if (status === "error") return <div style={{ color: colors.danger, fontSize: 14 }}>Couldn’t load the CRM. <button style={{ ...ghostBtn, marginLeft: 8 }} onClick={load}>Retry</button></div>;
  return (
    <div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
        {stat("contacts", "Contacts", counts.contacts)}
        {stat("lead", "Lead", counts.lead)}
        {stat("in_progress", "In Progress", counts.in_progress)}
        {stat("won", "Won", counts.won)}
        {stat("overdue", "Follow-ups overdue", counts.overdue, counts.overdue > 0)}
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
        <select aria-label="Status filter" style={{ ...inputStyle, width: "auto" }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
        </select>
        <select aria-label="Owner filter" style={{ ...inputStyle, width: "auto" }} value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)}>
          <option value="">Anyone working it</option>
          <option value="__none">Unassigned</option>
          <option value={CLIENT_OWNER}>Client</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.firstName || u.name || u.email}</option>)}
        </select>
        <span style={{ flex: 1 }} />
        <button style={ghostBtn} onClick={() => setModal("import")}>Import CSV</button>
        <button style={ghostBtn} onClick={() => { setLinkQuery(""); setModal("link"); }}>Link existing contact</button>
        <button style={primaryBtn} onClick={() => setModal("new")}>+ New contact</button>
      </div>
      {tracks.length === 0 ? (
        <div style={{ color: colors.textDim, fontSize: 14, padding: "30px 0", textAlign: "center" }}>No contacts on {clientName}’s CRM yet. Add one, link one from the shared book, or import a spreadsheet.</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>{["Pri", "Contact", "Status", "Goal", "Next step", "Working it"].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {shown.map((t) => {
                const c = contactOf(t);
                const over = isOverdue(t, today);
                const pm = PRIORITY_META[t.priority] || PRIORITY_META.med;
                return (
                  <tr key={t.id} onClick={() => setOpenId(t.id)} style={{ cursor: "pointer", background: t.id === openId ? colors.surfaceAlt : undefined }}>
                    <td style={td} onClick={stop}>
                      <select aria-label={`Priority for ${c?.name || "contact"}`} value={t.priority || "med"} onChange={(e) => save(t.id, { priority: e.target.value })}
                        style={{ ...pill, color: pm.color, fontWeight: 700 }}>
                        {PRIORITY_ORDER.map((p) => <option key={p} value={p} style={opt}>{PRIORITY_META[p].label}</option>)}
                      </select>
                    </td>
                    <td style={td}><b>{c?.name || "(deleted contact)"}</b>{c && <div style={{ fontSize: 11.5, color: colors.textDim }}>{[c.organization, c.role].filter(Boolean).join(" · ")}</div>}</td>
                    <td style={td} onClick={stop}>
                      <select aria-label={`Status for ${c?.name || "contact"}`} value={t.status} onChange={(e) => save(t.id, { status: e.target.value })}
                        style={{ ...pill, color: (STATUS_META[t.status] || STATUS_META.lead).color, background: (STATUS_META[t.status] || STATUS_META.lead).bg, fontWeight: 600 }}>
                        {STATUS_ORDER.map((s) => <option key={s} value={s} style={opt}>{STATUS_META[s].label}</option>)}
                      </select>
                    </td>
                    <td style={td}>{t.goal || <span style={{ fontSize: 11.5, color: colors.textDim }}>— not set</span>}</td>
                    <td style={td}>{t.nextStep || (t.nextStepDate ? "" : "—")}
                      {t.nextStepDate && <div style={{ fontSize: 11.5, color: over ? colors.danger : colors.textDim, fontWeight: over ? 600 : 400 }}>{fmtDate(t.nextStepDate)}{over ? " · overdue" : ""}</div>}</td>
                    <td style={td} onClick={stop}>
                      <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        {isClientOwner(t.ownerUserId) ? <ClientTag /> : t.ownerUserId && <UserAvatar user={user(t.ownerUserId)} />}
                        <select aria-label={`Working it for ${c?.name || "contact"}`} value={t.ownerUserId || ""} onChange={(e) => save(t.id, { ownerUserId: e.target.value || null })}
                          style={{ ...pill, color: t.ownerUserId ? colors.text : colors.textDim }}>
                          <option value="" style={opt}>Unassigned</option>
                          <option value={CLIENT_OWNER} style={opt}>Client</option>
                          {t.ownerUserId && !isClientOwner(t.ownerUserId) && !user(t.ownerUserId) && <option value={t.ownerUserId} style={opt}>Unknown</option>}
                          {users.map((u) => <option key={u.id} value={u.id} style={opt}>{u.firstName ? `${u.firstName} ${u.lastName || ""}`.trim() : u.name || u.email}</option>)}
                        </select>
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <TrackPanel key={open.id} track={open} contact={contactOf(open)} users={users}
          otherClientNames={allTracks.filter((t) => t.contactId === open.contactId && t.clientId !== clientId).map((t) => clientNameOf(t.clientId))}
          onSave={(fields) => save(open.id, fields)}
          onAddActivity={(text) => activityOp(() => api.crmTracks.addActivity(open.id, text))}
          onEditActivity={(entryId, text) => activityOp(() => api.crmTracks.editActivity(open.id, entryId, text))}
          onRemoveActivity={(entryId) => activityOp(() => api.crmTracks.removeActivity(open.id, entryId))}
          onEditContact={() => setEditContactId(open.contactId)}
          onRemove={() => setConfirmRemove(open)} onClose={() => setOpenId(null)} />
      )}
      {modal === "new" && (
        <Modal title={`New contact for ${clientName}`} onClose={() => setModal(null)}>
          <ContactForm contacts={contacts} mode="client" onCancel={() => setModal(null)} onLink={createTrack}
            onCreated={(c) => { setContacts((cs) => [...cs, c]); createTrack(c.id); }} />
        </Modal>
      )}
      {modal === "link" && (
        <Modal title="Link a contact from the shared book" onClose={() => setModal(null)}>
          <input autoFocus style={inputStyle} placeholder="Search name, organization, expertise…" value={linkQuery} onChange={(e) => setLinkQuery(e.target.value)} />
          <div style={{ maxHeight: "50vh", overflow: "auto", marginTop: 10 }}>
            {linkable.length === 0 && <div style={{ fontSize: 13, color: colors.textDim, padding: 8 }}>No matching contacts that aren’t already on this client.</div>}
            {linkable.map((c) => (
              <button key={c.id} onClick={() => createTrack(c.id)} style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: "none", borderBottom: `1px solid ${colors.border}`, padding: "8px 4px", cursor: "pointer", color: colors.text, fontSize: 13 }}>
                <b>{c.name}</b> <span style={{ color: colors.textDim, fontSize: 12 }}>{[c.organization, c.role].filter(Boolean).join(" · ")}</span>
              </button>
            ))}
          </div>
        </Modal>
      )}
      {modal === "import" && (
        <ImportCsv clientId={clientId} clientName={clientName} contacts={contacts} users={users} clientTracks={tracks}
          onClose={() => setModal(null)} onDone={(summary) => { setModal(null); flash?.("success", `Import done: ${summary}`); load(); }} />
      )}
      {editContactId && contacts.find((c) => c.id === editContactId) && (
        <ContactCard contact={contacts.find((c) => c.id === editContactId)} tracks={allTracks.filter((t) => t.contactId === editContactId)}
          clients={clients} users={users} flash={flash} onChanged={load} onClose={() => setEditContactId(null)} />
      )}
      {confirmRemove && (
        <ConfirmDialog title={`Remove ${contactOf(confirmRemove)?.name || "this contact"} from ${clientName}?`} confirmLabel="Remove"
          onConfirm={() => remove(confirmRemove)} onCancel={() => setConfirmRemove(null)}>
          This client’s status, goal and activity for them are deleted. They stay in the shared contact book.
        </ConfirmDialog>
      )}
    </div>
  );
}
